import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SCALE_STOPS = [
  { at: 1, rgb: [245, 223, 134] },  // light yellow
  { at: 7, rgb: [163, 226, 180] },  // light green
  { at: 10, rgb: [9, 184, 223] },   // Lookers blue
];

/** Colour for a point on the 1-10 condition scale: light yellow -> light green -> Lookers blue. */
export function greyShade(score) {
  const n = Math.max(1, Math.min(10, Number(score) || 1));
  for (let i = 0; i < SCALE_STOPS.length - 1; i += 1) {
    const a = SCALE_STOPS[i];
    const b = SCALE_STOPS[i + 1];
    if (n <= b.at) {
      const t = (n - a.at) / (b.at - a.at);
      const mix = a.rgb.map((c, k) => Math.round(c + (b.rgb[k] - c) * t));
      return `rgb(${mix.join(", ")})`;
    }
  }
  return `rgb(${SCALE_STOPS[SCALE_STOPS.length - 1].rgb.join(", ")})`;
}

/** Same hue, darkened enough to stay legible as text on white. */
export function scaleTextShade(score) {
  const match = /rgb\((\d+), (\d+), (\d+)\)/.exec(greyShade(score));
  if (!match) return "#52525B";
  const [r, g, b] = match.slice(1).map((v) => Math.round(Number(v) * 0.62));
  return `rgb(${r}, ${g}, ${b})`;
}

const SCALE = [
  { range: "10", label: "Like New", desc: "Presents virtually as it would in a retail store. No meaningful visible wear." },
  { range: "9", label: "Excellent", desc: "Extremely clean with minimal evidence of previous use." },
  { range: "8", label: "Very Good", desc: "Light signs of normal use but presents very well." },
  { range: "7", label: "Good", desc: "Visible signs of previous use but remains attractive and fully wearable." },
  { range: "6", label: "Fair", desc: "More noticeable wear. Fully usable but cosmetic wear is clearly visible." },
];

export function ConditionScaleContent() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-zinc-500">Every pair sold by Lookers is individually inspected and given a Condition Rating from 1 to 10 so you know exactly what you're buying — before you buy it.</p>
      {SCALE.map((s) => (
        <div key={s.range} className="flex gap-4 border-b border-zinc-100 pb-4 last:border-0">
          <div className="font-serif text-2xl w-10 shrink-0" style={{ color: scaleTextShade(Number(s.range)) }}>{s.range}</div>
          <div>
            <div className="font-heading text-xs uppercase tracking-wide mb-1 text-zinc-900">{s.label}</div>
            <div className="text-sm text-zinc-500">{s.desc}</div>
          </div>
        </div>
      ))}
      <p className="text-xs text-zinc-400 italic">Condition rating determined by Lookers. Frames rated below 6 are not offered through the main Lookers store.</p>
    </div>
  );
}

export function ConditionMeter({ score, label, size = "sm" }) {
  const tierColor = scaleTextShade(score);
  const dotClass = size === "sm" ? "h-1 w-1 rounded-full" : "h-1.5 w-2.5 rounded-full";
  const textClass = size === "sm" ? "text-[10px]" : "text-xs";
  return (
    <div className="flex items-center gap-2" data-testid="condition-meter">
      <div className="flex gap-[3px]">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className={dotClass} style={{ backgroundColor: i < score ? greyShade(i + 1) : "#E4E4E7" }} />
        ))}
      </div>
      <span className={`${textClass} font-mono font-bold uppercase tracking-wider whitespace-nowrap`} style={{ color: tierColor }}>{score}/10 {label}</span>
    </div>
  );
}

export function ConditionRatingCard({ score, label, imperfections = [], size = "default" }) {
  const [open, setOpen] = useState(false);
  const tierColor = scaleTextShade(score);
  return (
    <div className="border-2 border-neutral-900 rounded-2xl p-6 bg-white" data-testid="condition-rating-card">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-neutral-500">Condition Rating</span>
        <button onClick={() => setOpen(true)} className="text-[11px] font-mono uppercase tracking-wider underline text-teal-600 hover:text-teal-700" data-testid="condition-guide-link">
          What Does This Mean?
        </button>
      </div>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-heading font-bold text-5xl text-neutral-900" style={{ color: tierColor }}>{score}<span className="text-lg text-neutral-400">/10</span></span>
        <span className="text-xs font-mono font-bold uppercase tracking-widest px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: tierColor }}>{label}</span>
      </div>
      <div className="flex gap-1 mb-4">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className="h-2 flex-1 rounded-full" style={{ backgroundColor: i < score ? greyShade(i + 1) : "#E4E4E7" }} />
        ))}
      </div>
      {imperfections.length > 0 && (
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest mb-2 text-neutral-700">Things To Know</div>
          <ul className="space-y-1">
            {imperfections.map((imp) => (
              <li key={imp.id} className="text-xs text-neutral-500 flex gap-2">
                <span>—</span>
                <span>{imp.note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11px] text-neutral-400 mt-4">No surprises. Condition rating determined by Lookers.</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white text-neutral-900" data-testid="condition-guide-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl text-neutral-900">Our Condition Scale</DialogTitle>
          </DialogHeader>
          <ConditionScaleContent />
        </DialogContent>
      </Dialog>
    </div>
  );
}
